import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { getPurchaseSaleReport, getPurchaseSaleFilterOptions } from '../../services/reportApi';

const toYmd = (d) => {
    const date = new Date(d);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const datePresets = [
    { label: 'Today', fn: () => { const t = new Date(); return { start: toYmd(t), end: toYmd(t) }; } },
    { label: 'Yesterday', fn: () => { const t = new Date(); t.setDate(t.getDate() - 1); return { start: toYmd(t), end: toYmd(t) }; } },
    { label: 'Last 7 Days', fn: () => { const s = new Date(); s.setDate(s.getDate() - 6); return { start: toYmd(s), end: toYmd(new Date()) }; } },
    { label: 'Last 30 Days', fn: () => { const s = new Date(); s.setDate(s.getDate() - 29); return { start: toYmd(s), end: toYmd(new Date()) }; } },
    { label: 'This Month', fn: () => { const s = new Date(); s.setDate(1); return { start: toYmd(s), end: toYmd(new Date()) }; } },
    { label: 'Last Month', fn: () => { const s = new Date(); s.setMonth(s.getMonth() - 1, 1); const e = new Date(); e.setDate(0); return { start: toYmd(s), end: toYmd(e) }; } },
    { label: 'This Month Last Year', fn: () => { const s = new Date(); s.setFullYear(s.getFullYear() - 1, s.getMonth(), 1); const e = new Date(s.getFullYear(), s.getMonth() + 1, 0); return { start: toYmd(s), end: toYmd(e) }; } },
    { label: 'This Year', fn: () => { const s = new Date(new Date().getFullYear(), 0, 1); const e = new Date(); return { start: toYmd(s), end: toYmd(e) }; } },
    { label: 'Last Year', fn: () => { const y = new Date().getFullYear() - 1; return { start: `${y}-01-01`, end: `${y}-12-31` }; } },
    { label: 'Current Financial Year', fn: () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return { start: `${y}-04-01`, end: `${y + 1}-03-31` }; } },
    { label: 'Last Financial Year', fn: () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() - 1 : t.getFullYear() - 2; return { start: `${y}-04-01`, end: `${y + 1}-03-31` }; } },
    { label: 'Custom Range', fn: () => ({ start: '', end: '' }) },
];

const defaultPreset = datePresets.find(p => p.label === 'This Year');
const defaultDates = defaultPreset.fn();

const inputStyle = {
    padding: '0.4rem 0.6rem',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    color: '#334155',
    outline: 'none',
    minWidth: '120px',
};

const PurchaseSaleReport = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        start_date: defaultDates.start,
        end_date: defaultDates.end,
        location_id: '',
    });
    const [filterOptions, setFilterOptions] = useState({ locations: [] });
    const [showDateFilter, setShowDateFilter] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getPurchaseSaleReport(filters);
            if (res.success) setData(res.data);
            else setError(res.message || 'Failed to load report');
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
        getPurchaseSaleFilterOptions()
            .then(r => { if (r.success) setFilterOptions(r.data); })
            .catch(() => {});
    }, []);

    useEffect(() => { fetchData(); }, [filters]);

    const applyDatePreset = (preset) => {
        const { start, end } = preset.fn();
        setFilters(f => ({ ...f, start_date: start, end_date: end }));
        setShowDateFilter(false);
    };

    const purchases = data?.purchases || {};
    const sales = data?.sales || {};
    const overall = data?.overall || {};

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* Filter bar */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>

                {/* Date filter dropdown */}
                <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowDateFilter(s => !s)} style={{
                        ...inputStyle, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem',
                    }}>
                        <i className="fa fa-calendar" style={{ color: '#64748b', fontSize: '0.75rem' }} />
                        <span>Filter by date</span>
                        <i className="fa fa-chevron-down" style={{ color: '#94a3b8', fontSize: '0.6rem', marginLeft: '0.25rem' }} />
                    </button>
                    {showDateFilter && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, zIndex: 50,
                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '0.5rem 0', minWidth: '180px', marginTop: '4px',
                        }}>
                            {datePresets.map(p => (
                                <div key={p.label} onClick={() => applyDatePreset(p)} style={{
                                    padding: '0.4rem 0.85rem', fontSize: '0.8125rem', color: '#334155',
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                    background: p.label === 'This Year' ? '#eff6ff' : 'transparent',
                                    fontWeight: p.label === 'This Year' ? '600' : '400',
                                }} onMouseEnter={e => e.target.style.background = '#f1f5f9'} onMouseLeave={e => e.target.style.background = p.label === 'This Year' ? '#eff6ff' : 'transparent'}>
                                    {p.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />

                <select value={filters.location_id} onChange={e => setFilters(f => ({ ...f, location_id: e.target.value }))} style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }}>
                    <option value="">All Locations</option>
                    {Array.isArray(filterOptions.locations)
                        ? filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                        : Object.entries(filterOptions.locations || {}).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>

                {filters.start_date && filters.end_date && (
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {filters.start_date} to {filters.end_date}
                    </span>
                )}
            </div>

            {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b', fontSize: '0.875rem' }}>Loading...</div>}
            {error && <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', color: '#dc2626', borderRadius: '6px', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</div>}

            {!loading && (
                <>
                    {/* ── Two-column Metrics Grid ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>

                        {/* Purchases Card */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#475569', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                                Purchases
                            </div>
                            {[
                                { label: 'Total Purchase:', value: purchases.total_purchase_formatted || '$ 0.00', hi: true },
                                { label: 'Purchase Including tax:', value: purchases.purchase_inc_tax_formatted || '$ 0.00' },
                                { label: 'Total Purchase Return Including Tax:', value: purchases.total_purchase_return_formatted || '$ 0.00', hi: true },
                                { label: 'Purchase Due:', value: purchases.purchase_due_formatted || '$ 0.00' },
                            ].map((row, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.5rem', background: row.hi ? '#f8fafc' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>
                                    <span style={{ fontSize: '0.8125rem', color: '#334155', fontWeight: '500' }}>{row.label}</span>
                                    <span style={{ fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600' }}>{row.value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Sales Card */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#475569', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                                Sales
                            </div>
                            {[
                                { label: 'Total Sale:', value: sales.total_sale_formatted || '$ 0.00', hi: true },
                                { label: 'Sale Including tax:', value: sales.sale_inc_tax_formatted || '$ 0.00' },
                                { label: 'Total Sell Return Including Tax:', value: sales.total_sell_return_formatted || '$ 0.00', hi: true },
                                { label: 'Sale Due:', value: sales.sale_due_formatted || '$ 0.00' },
                            ].map((row, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.5rem', background: row.hi ? '#f8fafc' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>
                                    <span style={{ fontSize: '0.8125rem', color: '#334155', fontWeight: '500' }}>{row.label}</span>
                                    <span style={{ fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600' }}>{row.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Overall Summary Card ── */}
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: '#64748b' }}>
                            <span style={{ fontSize: '0.95rem' }}>Overall ((Sale - Sell Return) - (Purchase - Purchase Return))</span>
                            <i className="fa fa-info-circle" style={{ color: '#0ea5e9' }} />
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1rem', color: '#64748b' }}>
                                Sale - Purchase: <strong style={{ color: overall.sale_minus_purchase >= 0 ? '#22c55e' : '#ef4444' }}>{overall.sale_minus_purchase_formatted || '$ 0.00'}</strong>
                            </span>
                        </div>
                        <div>
                            <span style={{ fontSize: '1rem', color: '#64748b' }}>
                                Due amount: <strong style={{ color: overall.due_amount >= 0 ? '#22c55e' : '#ef4444' }}>{overall.due_amount_formatted || '$ 0.00'}</strong>
                            </span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PurchaseSaleReport;
